<script lang="ts">
  import { portal } from "svelte-portal"
  import { fly, fade } from "svelte/transition"
  import Close from "@autouncle/icons/dist/assets/close.svg?raw"
  import AUButtonIcon from "./AUButtonIcon.svelte"
  import { getContext } from "svelte"
  import { Writable } from "svelte/store"
  import { WrapperElType } from "website_script/stores/wrapper"
  import {
    deepActiveElement,
    findAllFocusableElements,
    releaseFocus,
    trapFocus,
  } from "component_library/lib/focus"

  export let closeLabel: string
  export let showFooter: boolean = true
  export let title: string
  export let description: string | undefined = undefined
  export let id: string
  export let isSlim: boolean = false

  let previouslyFocused: HTMLElement | null = null
  let contentEl: HTMLElement
  let expanded: boolean = false
  let originalBodyOverflow: string
  let originalHtmlOverflow: string

  const wrapperEl = getContext<Writable<WrapperElType> | undefined>(
    "wrapperElStore"
  )

  function lockScroll() {
    originalBodyOverflow = document.body.style.overflow
    originalHtmlOverflow = document.documentElement.style.overflow
    document.body.style.overflow = document.documentElement.style.overflow =
      "hidden"
  }

  function unlockScroll() {
    document.body.style.overflow = originalBodyOverflow
    document.documentElement.style.overflow = originalHtmlOverflow
  }

  function onKeyDown(event: KeyboardEvent) {
    if (event.key === "Escape") return close()
    if (event.key !== "Tab") return

    const container = event.currentTarget as HTMLElement
    const element = deepActiveElement()

    const focusables = findAllFocusableElements(container)
    // Check if element is in our focusable array
    if (!focusables.includes(element as HTMLElement)) return

    const first = focusables[0]
    const last = focusables[focusables.length - 1]

    // shift + tab on first element should wrap back to last
    if (element === first && event.shiftKey) {
      ;(last as HTMLElement).focus()
      event.preventDefault()
    }

    // plain tab on last element should wrap back to first
    if (element === last && !event.shiftKey) {
      ;(first as HTMLElement).focus()
      event.preventDefault()
    }
  }

  export const open = () => {
    if (expanded) return

    lockScroll()
    expanded = true
    previouslyFocused = document.activeElement as HTMLElement

    requestAnimationFrame(() => contentEl && trapFocus(contentEl, onKeyDown))
  }

  export const close = () => {
    if (!expanded) return

    releaseFocus(contentEl, onKeyDown)
    expanded = false
    unlockScroll()
    if (previouslyFocused) {
      previouslyFocused.focus()
    }
  }

  /** scrolls to the top of the contents of the modal */
  export const scrollToTop = () => {
    contentEl.scroll(0, 0)
  }

  // Conditionally apply portal if wrapperEl is available
  const portalIfAvailable = (node: HTMLElement) => {
    if ($wrapperEl) {
      return portal(node, $wrapperEl)
    }
    return {}
  }

  // THIS IS A HACK, PLEASE REMOVE
  // Restrict width of modal compared to default
  // Also extend height to 100% to avoid scroll
  const contentStyle = isSlim ? "max-width: 432px;" : ""
</script>

{#if expanded}
  <div
    {id}
    class="au-modal"
    role="dialog"
    aria-modal="true"
    tabindex="-1"
    aria-labelledby={`modal-title-${id}`}
    aria-describedby={description ? `modal-description-${id}` : undefined}
    use:portalIfAvailable
  >
    <div
      class="au-modal__backdrop"
      out:fade|global={{ duration: 250 }}
      aria-hidden="true"
    />
    <div
      class="au-modal__content"
      style={contentStyle}
      transition:fly|global={{ y: 200, duration: 400 }}
      bind:this={contentEl}
    >
      <h2 class="au-sr-only" id={`modal-title-${id}`}>{title}</h2>
      {#if description}
        <p class="au-sr-only" id={`modal-description-${id}`}>{description}</p>
      {/if}
      {#if $$slots.header}
        <div class="au-modal__header">
          <div class="au-modal__heading">
            <slot name="header" />
          </div>
          <div class="au-modal__close">
            <AUButtonIcon
              size="sm"
              sroText={closeLabel}
              icon={Close}
              onClick={() => close()}
            />
          </div>
        </div>
      {:else}
        <div class="au-modal__close">
          <AUButtonIcon
            size="sm"
            sroText={closeLabel}
            icon={Close}
            onClick={() => close()}
          />
        </div>
      {/if}

      <div class="au-modal__main-content">
        <slot />
      </div>

      {#if $$slots.footer && showFooter}
        <div class="au-modal__footer">
          <slot name="footer" />
        </div>
      {/if}
    </div>
  </div>
{/if}

<style>
  .au-modal {
    position: fixed;
    top: 0;
    left: 0;
    z-index: var(--au-z-index-modals);
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    box-shadow: var(--au-box-default-box-shadow);
    background: transparent;
  }

  .au-modal__backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--au-backdrop-background-color);
    z-index: var(--au-z-index-backdrop);
    opacity: var(--au-backdrop-opacity);
    transition: opacity var(--au-transition-fast) ease-in-out,
      visibility var(--au-transition-fast) ease-in-out;
    pointer-events: none;
  }

  .au-modal__content {
    position: relative;
    display: flex;
    flex-direction: column;
    min-height: 6rem;
    max-height: 100%;
    width: 100%;
    background-color: var(--au-background-default);
    box-shadow: var(--au-box-default-box-shadow);
    z-index: var(--au-z-index-modals);
    border-radius: var(--au-border-radius-sharp);
    overflow: hidden;
  }

  .au-modal__header {
    position: sticky;
    top: 0;
    padding-block: var(--au-spacing-lg);
    padding-inline: var(--au-spacing-lg);
    border-bottom: var(--au-line-width-default) solid
      var(--au-line-color-neutral);
  }

  .au-modal__header .au-modal__heading {
    font-weight: var(--au-text-weight-bold);
    font-size: var(--au-text-heading-2-font-size);
    line-height: var(--au-text-heading-2-line-height);

    text-align: center;

    /* Align title to the center while giving space for close button on the side */
    padding-inline: var(--au-tappable-size-lg);
  }

  .au-modal__header .au-modal__close {
    position: absolute;
    right: var(--au-spacing-md);
    top: var(--au-spacing-md);
  }

  .au-modal__footer {
    position: sticky;
    bottom: 0;
    padding-block: var(--au-spacing-md);
    padding-inline: var(--au-spacing-md);
    border-top: var(--au-line-width-default) solid var(--au-line-color-neutral);
  }

  .au-modal__main-content {
    position: relative;
    padding-block: var(--au-spacing-lg);
    padding-inline: var(--au-spacing-lg);
    overflow-y: auto;
    flex-grow: 1;
  }

  /* When there is no header, close button is absolute to content */
  .au-modal__content > .au-modal__close {
    position: absolute;
    right: var(--au-spacing-xs);
    top: var(--au-spacing-xs);
    z-index: 1;
  }
  .au-modal__content > .au-modal__close :global(.au-button-icon) {
    box-shadow: var(--box-default-box-shadow);
  }

  @media (min-width: 480px) {
    .au-modal__content {
      max-width: 560px;
      max-height: 90vh;
    }
  }
</style>
